import { Link } from 'react-router-dom'
import { Mail, Phone } from 'lucide-react'

function WhatsAppIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  )
}

// Real Tvantra brand mark (cropped from the brand kit). Shown as-is in both
// themes — no theme-adaptive fallback, matching what the brand asset shows.
function LogoMark() {
  return (
    <img
      src="/tvantra-mark.png"
      alt=""
      width={28}
      height={28}
      style={{ width: 28, height: 28, objectFit: 'contain' }}
    />
  )
}

const LINKS = {
  Product: [
    { label: 'Features', to: '/features' },
    { label: 'Products', to: '/products' },
  ],
  Company: [
    { label: 'About',   to: '/about'   },
    { label: 'Contact', to: '/contact' },
    { label: 'Blog',    to: '/blog'    },
  ],
  Modules: [
    { label: 'Feeding',    to: '/features#feeding'    },
    { label: 'Attendance', to: '/features#attendance' },
    { label: 'Academics',  to: '/features#academics'  },
    { label: 'Finance',    to: '/features#finance'    },
  ],
  Legal: [
    { label: 'Privacy Policy',   to: '/privacy' },
    { label: 'Terms of Service', to: '/terms'   },
  ],
}

export default function Footer() {
  return (
    <footer className="border-t border-theme bg-sunk">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-10">
          {/* Brand */}
          <div className="col-span-2">
            <Link to="/" className="flex items-center gap-2 mb-1">
              <LogoMark />
              <span className="font-display font-bold text-base text-ink">
                MySchoolPortal
              </span>
            </Link>
            <p className="text-xs text-[var(--ink-soft)] ml-10">by Tvantra</p>
            <a
              href="tel:+233596539060"
              className="flex items-center gap-2 mt-4 text-sm font-semibold text-ink hover:text-accent transition-colors duration-150 w-fit"
            >
              <Phone size={14} className="text-accent" />
              +233 59 653 9060
            </a>
            <div className="flex gap-3 mt-6">
              {[
                { icon: <WhatsAppIcon />, href: 'https://wa.me/233596539060?text=' + encodeURIComponent("Hello! I'm interested in MySchoolPortal. Can you tell me more?"), label: 'WhatsApp' },
                { icon: <Mail size={16} />, href: 'mailto:info@myschoolportal.xyz', label: 'Email' },
              ].map((s, i) => (
                <a
                  key={i}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  className="w-9 h-9 rounded flex items-center justify-center border border-theme text-[var(--ink-soft)] hover:text-accent hover:border-theme-strong transition-colors duration-150"
                >
                  {s.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Link columns */}
          {Object.entries(LINKS).map(([heading, items]) => (
            <div key={heading}>
              <p className="text-xs font-semibold text-[var(--ink-soft)] tracking-widest uppercase mb-4">{heading}</p>
              <ul className="space-y-3">
                {items.map(({ label, to }) => (
                  <li key={label}>
                    <Link
                      to={to}
                      className="text-sm text-[var(--ink-soft)] hover:text-accent transition-colors duration-150"
                    >
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-16 pt-8 border-t border-theme flex flex-col sm:flex-row items-center justify-between gap-4">
          {/* suppressHydrationWarning: new Date() runs at SSG build time and again
              on the client, so the year can differ across a year boundary and
              triggers a React #418 hydration mismatch. The copyright year is
              cosmetic, so we let the client value stand without warning. */}
          <p className="text-sm text-[var(--ink-soft)]" suppressHydrationWarning>
            &copy; {new Date().getFullYear()} Tvantra. All rights reserved. MySchoolPortal is a product of Tvantra.
          </p>
        </div>
      </div>
    </footer>
  )
}
